import { Link } from "react-router-dom";
import { MapPin, Building2, ShieldCheck, Heart, GitCompare, ArrowRight } from "lucide-react";
import { useCompare } from "../App";
import { useAuth } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";

const formatPrice = (price) => {
  if (price >= 10000000) {
    return `₹${(price / 10000000).toFixed(2)} Cr`;
  } else if (price >= 100000) {
    return `₹${(price / 100000).toFixed(2)} L`;
  }
  return `₹${price.toLocaleString("en-IN")}`;
};

const PropertyCard = ({ property, variant = "default" }) => {
  const { addToCompare, isInCompare, removeFromCompare } = useCompare();
  const { user, token } = useAuth();
  
  const inCompare = isInCompare(property.property_id);

  const handleCompare = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (inCompare) {
      removeFromCompare(property.property_id);
    } else {
      addToCompare(property);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      toast.error("Please login to save properties");
      return;
    }

    try {
      await axios.post(
        `${API}/saved-properties/${property.property_id}`,
        {},
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      toast.success("Property saved!");
    } catch (error) {
      if (error.response?.status === 400) {
        toast.info("Property already saved");
      } else {
        toast.error("Failed to save property");
      }
    }
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case "new_launch":
        return "New Launch";
      case "under_construction":
        return "Under Construction";
      case "ready_to_move":
        return "Ready to Move";
      default:
        return category;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case "new_launch":
        return "bg-amber-500 text-white";
      case "ready_to_move":
        return "bg-emerald-600 text-white";
      default:
        return "bg-stone-600 text-white";
    }
  };

  if (variant === "featured") {
    return (
      <Link
        to={`/property/${property.property_id}`}
        className="property-card group col-span-1 md:col-span-2 row-span-2 relative"
        data-testid={`property-card-${property.property_id}`}
      >
        {/* Image */}
        <div className="relative h-full min-h-[400px] overflow-hidden">
          <img
            src={property.images?.[0] || "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800"}
            alt={property.name}
            className="property-image w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-stone-900/90 via-stone-900/30 to-transparent" />
          
          {/* Badges */}
          <div className="absolute top-4 left-4 flex items-center gap-2">
            <span className={`text-xs px-3 py-1 uppercase tracking-wider font-medium ${getCategoryColor(property.category)}`}>
              {getCategoryLabel(property.category)}
            </span>
            {property.featured && (
              <span className="bg-amber-500 text-white text-xs px-3 py-1 uppercase tracking-wider font-medium">
                Featured
              </span>
            )}
          </div>

          {/* Actions */}
          <div className="absolute top-4 right-4 flex items-center gap-2">
            <button
              onClick={handleSave}
              className="p-2 bg-white/10 backdrop-blur-sm rounded-full hover:bg-white/20 transition-colors"
              data-testid={`save-btn-${property.property_id}`}
            >
              <Heart className="w-5 h-5 text-white" strokeWidth={1.5} />
            </button>
            <button
              onClick={handleCompare}
              className={`p-2 rounded-full transition-colors ${
                inCompare ? "bg-amber-500 text-white" : "bg-white/10 backdrop-blur-sm hover:bg-white/20"
              }`}
              data-testid={`compare-btn-${property.property_id}`}
            >
              <GitCompare className={`w-5 h-5 ${inCompare ? "" : "text-white"}`} strokeWidth={1.5} />
            </button>
          </div>

          {/* Content */}
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <div className="mb-2">
              <span className="rera-badge font-mono text-xs">
                <ShieldCheck className="w-3 h-3 inline mr-1" strokeWidth={1.5} />
                RERA: {property.rera_id.slice(0, 20)}...
              </span>
            </div>
            <h3 className="font-display text-2xl md:text-3xl text-white mb-2">{property.name}</h3>
            <p className="text-white/80 flex items-center gap-1 mb-3">
              <MapPin className="w-4 h-4" strokeWidth={1.5} />
              {property.locality}, {property.city}
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <span className="text-white font-mono text-xl">
                {formatPrice(property.price_min)} - {formatPrice(property.price_max)}
              </span>
              <span className="text-white/60 text-sm">
                {property.configurations.join(" | ")}
              </span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/property/${property.property_id}`}
      className="property-card group"
      data-testid={`property-card-${property.property_id}`}
    >
      {/* Image */}
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={property.images?.[0] || "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800"}
          alt={property.name}
          className="property-image w-full h-full object-cover"
        />
        
        {/* Badges */}
        <div className="absolute top-3 left-3 flex items-center gap-2">
          <span className={`text-xs px-2 py-1 uppercase tracking-wider font-medium ${getCategoryColor(property.category)}`}>
            {getCategoryLabel(property.category)}
          </span>
        </div>

        {/* Actions */}
        <div className="absolute top-3 right-3 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={handleSave}
            className="p-2 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors shadow-sm"
            data-testid={`save-btn-${property.property_id}`}
          >
            <Heart className="w-4 h-4 text-stone-700" strokeWidth={1.5} />
          </button>
          <button
            onClick={handleCompare}
            className={`p-2 rounded-full transition-colors shadow-sm ${
              inCompare ? "bg-amber-500 text-white" : "bg-white/90 backdrop-blur-sm hover:bg-white"
            }`}
            data-testid={`compare-btn-${property.property_id}`}
          >
            <GitCompare className={`w-4 h-4 ${inCompare ? "" : "text-stone-700"}`} strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* RERA Badge */}
        <div className="mb-2">
          <span className="rera-badge font-mono text-xs">
            <ShieldCheck className="w-3 h-3 inline mr-1" strokeWidth={1.5} />
            RERA Verified
          </span>
        </div>

        {/* Title & Location */}
        <h3 className="font-display text-lg text-stone-900 mb-1 line-clamp-1 group-hover:text-emerald-900 transition-colors">
          {property.name}
        </h3>
        <p className="text-stone-500 text-sm flex items-center gap-1 mb-3">
          <MapPin className="w-3 h-3" strokeWidth={1.5} />
          {property.locality}, {property.city}
        </p>

        {/* Price & Config */}
        <div className="flex items-end justify-between">
          <div>
            <p className="font-mono text-lg text-stone-900">
              {formatPrice(property.price_min)}
              {property.price_max !== property.price_min && (
                <span className="text-stone-400"> - {formatPrice(property.price_max)}</span>
              )}
            </p>
            <p className="text-stone-500 text-xs mt-1">
              {property.configurations.slice(0, 3).join(" | ")}
            </p>
          </div>
          <span className="text-emerald-700 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
            View <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </span>
        </div>
      </div>
    </Link>
  );
};

export default PropertyCard;
